/**
 * Canonical Audit Analysis Schema — deterministic normalization.
 * =============================================================
 * Converts the raw, ephemeral Audit state (AuditProvider) into a clean,
 * self-describing semantic object: `auditAnalysisInput`.
 *
 * PURPOSE
 *   The future AI Full-Report pipeline should NOT have to decode opaque
 *   stored IDs (w8, pause index 4, Space cell 7, gesture id `count`,
 *   Structure array positions, …). This module resolves every stored value
 *   into its exact user-facing / observable semantic meaning, using the
 *   Audit's current implementation as the single source of truth.
 *
 * STRICT SCOPE
 *   - Deterministic data normalization ONLY.
 *   - No AI analysis, no conclusions, no report copy.
 *   - No scores, no quality judgements, no correctness logic.
 *   - Does NOT read patternRead, reportSubmission, selectedPresenter,
 *     UI/progress fields or legacy/dead fields.
 *   - Does NOT generate an auditId (added later with persistence).
 *
 * The config maps below are copied verbatim from the room components so this
 * module is self-contained (the room files do not export their constants).
 * They must stay in sync with:
 *   RehearsalRoom.jsx, RehearsalRoomStructure.jsx, RehearsalRoomVoice.jsx,
 *   RehearsalRoomGesture.jsx, RehearsalRoomSpace.jsx, RehearsalRoomOpening.jsx,
 *   RehearsalRoomReflection.jsx
 */

// ---------------------------------------------------------------------------
// MESSAGE (Room 01) — RehearsalRoom.jsx
// ---------------------------------------------------------------------------
const MESSAGE_NOTES = [
  { id: "audience",   label: "Audience",           hint: "Who am I speaking to?" },
  { id: "goal",       label: "Goal",               hint: "What am I trying to achieve?" },
  { id: "think",      label: "Think",              hint: "What should they think differently?" },
  { id: "feel",       label: "Feel",               hint: "What should they feel?" },
  { id: "do",         label: "Do",                 hint: "What should they do afterwards?" },
  { id: "coreIdea",   label: "Core Idea",          hint: "What is the one idea to remember?" },
  { id: "whyNow",     label: "Why Now",            hint: "Why does this matter now?" },
  { id: "stakes",     label: "Stakes",             hint: "What matters to them?" },
  { id: "whatToSay",  label: "What I Want to Say", hint: "What do I want to communicate?" },
  { id: "evidence",   label: "Evidence",           hint: "What facts or data do I need to include?" },
  { id: "coverage",   label: "Content Coverage",   hint: "What information must I cover?" },
  { id: "time",       label: "Time",               hint: "How much time do I have?" },
];

const MESSAGE_REFLECTION = {
  question: "What guided your choices most?",
  options: [
    { id: "audience",    label: "The audience",          supportingText: "What they need from me" },
    { id: "outcome",     label: "The outcome",           supportingText: "What I want the presentation to achieve" },
    { id: "message",     label: "The message",           supportingText: "What I most want to say" },
    { id: "content",     label: "The content",           supportingText: "What information I need to cover" },
    { id: "constraints", label: "Practical constraints", supportingText: "Time and what I can realistically include" },
  ],
};

// ---------------------------------------------------------------------------
// STRUCTURE (Room 02) — RehearsalRoomStructure.jsx
// ---------------------------------------------------------------------------
const STRUCTURE_CARDS = [
  { id: "context",        label: "Context" },
  { id: "problem",        label: "Problem" },
  { id: "story",          label: "Story" },
  { id: "evidence",       label: "Evidence" },
  { id: "solution",       label: "Solution" },
  { id: "recommendation", label: "Recommendation" },
  { id: "callToAction",   label: "Call to Action" },
];

const STRUCTURE_REFLECTION = {
  question: "What guided the order you created most?",
  options: [
    { id: "clarity",          label: "Clarity",           supportingText: "Making the story easy to follow" },
    { id: "logic",            label: "Logic",             supportingText: "Putting each idea where it made most sense" },
    { id: "persuasion",       label: "Persuasion",        supportingText: "Building toward the conclusion or action" },
    { id: "audienceInterest", label: "Audience interest", supportingText: "Keeping people engaged as the story develops" },
    { id: "instinct",         label: "Instinct",          supportingText: "It simply felt like the natural order" },
  ],
};

// ---------------------------------------------------------------------------
// VOICE (Room 03) — RehearsalRoomVoice.jsx
// ---------------------------------------------------------------------------
// Exact sentence + tokenisation (identical to the room's SENTENCE.split(" ")).
const VOICE_SENTENCE = "We don\u2019t need more information, we need a decision.";
const VOICE_WORDS = VOICE_SENTENCE.split(" ").map((w, i) => ({ id: `w${i}`, text: w }));

const VOICE_REFLECTION = {
  question: "What guided your emphasis and pauses most?",
  options: [
    { id: "meaning",           label: "Meaning",            supportingText: "Making the key idea clearer" },
    { id: "importantWords",    label: "Important words",    supportingText: "Making certain words stand out" },
    { id: "rhythm",            label: "Rhythm",             supportingText: "Making the sentence feel more natural" },
    { id: "audienceAttention", label: "Audience attention", supportingText: "Helping people stay with the message" },
    { id: "instinct",          label: "Instinct",           supportingText: "It just felt like the way I would say it" },
  ],
};

// ---------------------------------------------------------------------------
// GESTURE (Room 04) — RehearsalRoomGesture.jsx
// ---------------------------------------------------------------------------
const GESTURE_ROUNDS = [
  { round: 1, phraseId: "three_priorities",      phrase: "There are three priorities we need to focus on." },
  { round: 2, phraseId: "move_stronger_position", phrase: "We need to move from the current situation to a stronger position." },
  { round: 3, phraseId: "two_paths",             phrase: "These are the two paths in front of us." },
  { round: 4, phraseId: "bring_you_in",          phrase: "I\u2019d like to bring you into this conversation." },
];

// Verified observable visual meaning of each gesture asset (confirmed by
// inspecting /audit/gestures/*.png). Describes ONLY what is visibly happening
// — no quality / correctness judgement.
const GESTURE_OBSERVABLE_MEANING = {
  count:               "right hand raised showing three extended fingers",
  frame:               "both hands forming a framing box / L-shape",
  open_palms:          "hands near shoulder height, palms facing viewer",
  neutral:             "hands relaxed at sides, no specific gesture",
  expand_outward:      "arms extending sideways/outward",
  move_upward:         "one hand high and one lower, showing low-to-high progression",
  show_level:          "one flat palm-down hand indicating a single level",
  contrast_left_right: "hands to either side, palms up, presenting two options",
  divide_space:        "vertical/chopping hand visibly partitioning space",
  open_invitation:     "arms wide and forward in a welcoming/inviting posture",
};

const GESTURE_REFLECTION = {
  question: "What guided you most when choosing a gesture?",
  options: [
    { id: "words",    label: "The words themselves" },
    { id: "meaning",  label: "The meaning I wanted to make clearer" },
    { id: "audience", label: "How I imagined the audience receiving the message" },
    { id: "natural",  label: "What felt most natural to me" },
  ],
};

// ---------------------------------------------------------------------------
// SPACE (Room 05) — RehearsalRoomSpace.jsx
// ---------------------------------------------------------------------------
const SPACE_MOMENT_ORDER = ["opening", "tension", "video", "cta"];

// Cell 1–9 → physical stage geometry. Row-major, back(top) → front(bottom).
// `audienceProximity` describes PHYSICAL stage proximity only (far | mid |
// near) — it is NOT a connection/impact/quality signal.
const SPACE_CELL_GEOMETRY = {
  1: { depth: "back",   column: "left",   audienceProximity: "far" },
  2: { depth: "back",   column: "centre", audienceProximity: "far" },
  3: { depth: "back",   column: "right",  audienceProximity: "far" },
  4: { depth: "middle", column: "left",   audienceProximity: "mid" },
  5: { depth: "middle", column: "centre", audienceProximity: "mid" },
  6: { depth: "middle", column: "right",  audienceProximity: "mid" },
  7: { depth: "front",  column: "left",   audienceProximity: "near" },
  8: { depth: "front",  column: "centre", audienceProximity: "near" },
  9: { depth: "front",  column: "right",  audienceProximity: "near" },
};

const SPACE_REFLECTION = {
  question: "What guided those choices most?",
  options: [
    { id: "meaning",   label: "The meaning of each moment" },
    { id: "attention", label: "Keeping the audience\u2019s attention" },
    { id: "stage",     label: "Making room for what was happening on stage" },
    { id: "natural",   label: "What felt most natural to me" },
  ],
};

// ---------------------------------------------------------------------------
// OPENING (Room 06) — RehearsalRoomOpening.jsx
// ---------------------------------------------------------------------------
const OPENING_CHOICES = [
  { id: "welcome",    label: "Welcome & Introduce Myself", sampleLine: "Good morning. I\u2019m Alex, and today I\u2019d like to talk with you about something that matters to all of us." },
  { id: "curiosity",  label: "Curiosity",                  sampleLine: "What if the way we usually think about this is only part of the story?" },
  { id: "relevance",  label: "Relevance",                  sampleLine: "What we\u2019re going to look at today has a direct impact on what happens next." },
  { id: "tension",    label: "Tension / Challenge",        sampleLine: "We\u2019re facing a choice, and doing nothing is also a decision." },
  { id: "story",      label: "Story",                      sampleLine: "Not long ago, something happened that completely changed the way I looked at this." },
  { id: "bold_claim", label: "Bold Claim",                 sampleLine: "The biggest obstacle may not be the one we think it is." },
];

const OPENING_REFLECTION = {
  question: "What matters most to you in your opening?",
  options: [
    { id: "clarity",     label: "Clarity first" },
    { id: "connection",  label: "Connection first" },
    { id: "interest",    label: "Interest first" },
    { id: "credibility", label: "Credibility first" },
    { id: "momentum",    label: "Momentum first" },
  ],
};

// ---------------------------------------------------------------------------
// FINAL REFLECTIONS (Room 07) — RehearsalRoomReflection.jsx
// ---------------------------------------------------------------------------
const FINAL_QUESTIONS = {
  reflection_q1: "What did you notice about yourself while going through the audit?",
  reflection_q2: "Which part of your communication would you most like to explore or improve next?",
  reflection_q3: "Is there anything about your communication, your work, or the situations you speak in that you think would help us understand your results better?",
};

// ---------------------------------------------------------------------------
// Resolver helpers
// ---------------------------------------------------------------------------
const byId = (list, id) => list.find((x) => x.id === id) || null;

// Resolve a single-choice reflection stored as { question, selected, tag }.
// `withSupportingText` controls whether the schema includes supportingText
// (message/structure/voice) or only { selected, label } (gesture/space/opening).
function resolveReflection(stored, config, withSupportingText) {
  const selected = stored?.selected ?? null;
  if (!selected) {
    return withSupportingText
      ? { selected: null, label: null, supportingText: null }
      : { selected: null, label: null };
  }
  const opt = byId(config.options, selected);
  return withSupportingText
    ? { selected, label: opt?.label ?? null, supportingText: opt?.supportingText ?? null }
    : { selected, label: opt?.label ?? null };
}

// ---------------------------------------------------------------------------
// Per-room normalizers
// ---------------------------------------------------------------------------
function normalizeMessage(answers, reflections) {
  const m = answers?.message || {};
  const resolveNotes = (ids) =>
    (ids || [])
      .map((id) => byId(MESSAGE_NOTES, id))
      .filter(Boolean)
      .map((n) => ({ id: n.id, label: n.label, hint: n.hint }));

  const forNow = m.forNow || [];
  const forLater = m.forLater || [];
  // Every catalog note placed in NEITHER category — deterministic, and kept
  // distinct from forLater (no explicit placement, not "for later").
  const unassigned = MESSAGE_NOTES
    .filter((n) => !forNow.includes(n.id) && !forLater.includes(n.id))
    .map((n) => ({ id: n.id, label: n.label, hint: n.hint }));

  return {
    forNow: resolveNotes(forNow),
    forLater: resolveNotes(forLater),
    unassigned,
    reflection: resolveReflection(reflections?.message, MESSAGE_REFLECTION, true),
  };
}

function normalizeStructure(answers, reflections) {
  const s = answers?.structure || {};
  const sequence = Array.isArray(s.sequence) ? s.sequence : [];

  const placedCards = sequence
    .map((cardId, idx) => ({ cardId, position: idx + 1 }))
    .filter((x) => x.cardId) // drop null slots
    .map((x) => {
      const card = byId(STRUCTURE_CARDS, x.cardId);
      return { position: x.position, id: x.cardId, label: card?.label ?? null };
    });

  const placedIds = placedCards.map((c) => c.id);
  const unusedCards = STRUCTURE_CARDS
    .filter((c) => !placedIds.includes(c.id))
    .map((c) => ({ id: c.id, label: c.label }));

  return {
    placedCards,
    unusedCards,
    reflection: resolveReflection(reflections?.structure, STRUCTURE_REFLECTION, true),
  };
}

function normalizeVoice(answers, reflections) {
  const v = answers?.voice || {};
  const wordText = (wordId) => VOICE_WORDS.find((w) => w.id === wordId)?.text ?? null;

  const emphasis = (v.emphasis || []).map((wordId) => ({
    wordId,
    word: wordText(wordId),
  }));

  const pauses = (v.pauses || []).map((gapIndex) => ({
    gapIndex,
    afterWord: VOICE_WORDS[gapIndex]?.text ?? null,
    beforeWord: VOICE_WORDS[gapIndex + 1]?.text ?? null,
  }));

  return {
    sentence: VOICE_SENTENCE,
    emphasis,
    pauses,
    reflection: resolveReflection(reflections?.voice, VOICE_REFLECTION, true),
    scope: "emphasis_and_pause_choices_only",
  };
}

function normalizeGesture(answers, reflections) {
  const g = answers?.gesture || {};
  const picks = (g.picks || []).map((p) => {
    const roundCfg = GESTURE_ROUNDS.find((r) => r.phraseId === p.phraseId);
    return {
      round: p.round ?? roundCfg?.round ?? null,
      phraseId: p.phraseId ?? null,
      phrase: roundCfg?.phrase ?? null,
      selectedGesture: {
        id: p.selected ?? null,
        observableMeaning: GESTURE_OBSERVABLE_MEANING[p.selected] ?? null,
      },
    };
  });

  return {
    picks,
    reflection: resolveReflection(reflections?.gesture, GESTURE_REFLECTION, false),
    scope: "meaning_to_gesture_choice_only",
  };
}

function normalizeSpace(answers, reflections) {
  const s = answers?.space || {};
  const positions = s.positions || {};

  const resolveMoment = (moment) => {
    const cell = positions[moment];
    if (!cell) return null;
    const geo = SPACE_CELL_GEOMETRY[cell] || {};
    return {
      moment,
      cell,
      depth: geo.depth ?? null,
      column: geo.column ?? null,
      audienceProximity: geo.audienceProximity ?? null,
    };
  };

  const moments = SPACE_MOMENT_ORDER.map(resolveMoment).filter(Boolean);
  const path = moments.map((m, i) => ({ order: i + 1, ...m }));

  return {
    moments,
    path,
    reflection: resolveReflection(reflections?.space, SPACE_REFLECTION, false),
    scope: "intended_spatial_choices_only",
  };
}

function normalizeOpening(answers, reflections) {
  const o = answers?.opening || {};
  const choice = o.selected ? byId(OPENING_CHOICES, o.selected) : null;

  return {
    selected: {
      id: o.selected ?? null,
      label: choice?.label ?? null,
      sampleLine: choice?.sampleLine ?? null,
    },
    reflection: resolveReflection(reflections?.opening, OPENING_REFLECTION, false),
    scope: "preferred_opening_approach_only",
  };
}

function normalizeFinalReflections(answers, reflections) {
  const fallback = answers?.reflection || {};
  const build = (key, fallbackText) => {
    const stored = reflections?.[key] || null;
    return {
      question: stored?.question ?? FINAL_QUESTIONS[key],
      text: (stored?.notes ?? fallbackText ?? "") || "",
      dataType: "SELF_REPORT",
    };
  };

  return {
    selfObservation:   build("reflection_q1", fallback.q1),
    developmentGoal:   build("reflection_q2", fallback.q2),
    additionalContext: build("reflection_q3", fallback.q3),
  };
}

// ---------------------------------------------------------------------------
// Public API — build the canonical auditAnalysisInput
// ---------------------------------------------------------------------------
export function buildAuditAnalysisInput(state) {
  const s = state || {};
  const answers = s.answers || {};
  const reflections = s.reflections || {};

  return {
    startedAt: s.startedAt ?? null,
    completedAt: s.completedAt ?? null,

    message:   normalizeMessage(answers, reflections),
    structure: normalizeStructure(answers, reflections),
    voice:     normalizeVoice(answers, reflections),
    gesture:   normalizeGesture(answers, reflections),
    space:     normalizeSpace(answers, reflections),
    opening:   normalizeOpening(answers, reflections),

    finalReflections: normalizeFinalReflections(answers, reflections),

    // Traceability only — raw namespaced tags kept for backward-compat/debug.
    // Future AI stages should rely on the normalized fields above, NOT tags.
    _traceability: { tags: Array.isArray(s.tags) ? s.tags : [] },
  };
}

// ---------------------------------------------------------------------------
// DEV-ONLY inspection hook
// ---------------------------------------------------------------------------
// Publishes the canonical auditAnalysisInput to `window` so we can view/copy
// it after a completed Audit. Gated on non-production so no debug data is ever
// exposed in the production build/UI.
export function publishAuditAnalysisInputForDev(state) {
  if (typeof window === "undefined") return;
  if (typeof process !== "undefined" && process.env && process.env.NODE_ENV === "production") return;

  try {
    const input = buildAuditAnalysisInput(state);
    window.__auditAnalysisInput = input;
    window.getAuditAnalysisInput = () => input;
    window.copyAuditAnalysisInput = () => {
      const json = JSON.stringify(input, null, 2);
      if (typeof navigator !== "undefined" && navigator.clipboard) {
        navigator.clipboard.writeText(json).catch(() => {});
      }
      // eslint-disable-next-line no-console
      console.log(json);
      return json;
    };
  } catch (e) {
    // Normalization is best-effort in dev; never throw into the render loop.
  }
}

export const __config = {
  MESSAGE_NOTES,
  MESSAGE_REFLECTION,
  STRUCTURE_CARDS,
  STRUCTURE_REFLECTION,
  VOICE_SENTENCE,
  VOICE_WORDS,
  VOICE_REFLECTION,
  GESTURE_ROUNDS,
  GESTURE_OBSERVABLE_MEANING,
  GESTURE_REFLECTION,
  SPACE_MOMENT_ORDER,
  SPACE_CELL_GEOMETRY,
  SPACE_REFLECTION,
  OPENING_CHOICES,
  OPENING_REFLECTION,
  FINAL_QUESTIONS,
};
